import React, { useCallback, useEffect, useMemo, useState } from "react";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import TuneIcon from "@mui/icons-material/Tune";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Link,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link as RouterLink } from "react-router-dom";
import Logger from "../../../services/Logger";
import UtilService from "../../../services/UtilService";
import CustomSnackbar from "../../components/CustomSnackbar";
import CreditService, { creditProductDisplayLabel } from "../credit/CreditService";
import AppCodeSelect from "../credit/AppCodeSelect";
import {
    formatUsageLogSizeDifficulty,
    usageLogPuzzleTitle,
} from "../credit/creditUsageDisplay";

const TAB_WALLETS = 0;
const TAB_IAP = 1;
const TAB_AD = 2;
const TAB_USAGE = 3;

function formatEpochMs(value) {
    if (value == null || value === 0) return "—";
    return UtilService.formatMDY(value) || "—";
}

function apiFailureMessage(result, fallback = "Request failed") {
    if (result && typeof result === "object" && result.code === "FAILURE") {
        return result.message || fallback;
    }
    return null;
}

function formatCredits(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "—";
    }
    return Number(value).toLocaleString();
}

function resolveWalletBalance(appCode, walletSummary, rows) {
    const fromSummary = walletSummary.find((wallet) => wallet.appCode === appCode);
    if (fromSummary?.creditBalance != null) return Number(fromSummary.creditBalance);
    const fromRows = rows.find((row) => row.appCode === appCode);
    if (fromRows?.creditBalance != null) return Number(fromRows.creditBalance);
    return 0;
}

/**
 * Per-user mobile app credit wallets, IAP log, and usage for admin subscription page.
 */
export default function MobileCreditsAdmin({ cloudUserId }) {
    const [tab, setTab] = useState(TAB_WALLETS);
    const [loading, setLoading] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);
    const [walletSummary, setWalletSummary] = useState([]);
    const [adRewardByApp, setAdRewardByApp] = useState({});

    const [pageModel, setPageModel] = useState({ page: 0, pageSize: 25 });
    const [sort, setSort] = useState([{ field: "updatedAt", sort: "desc" }]);
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);

    const [filterAppCode, setFilterAppCode] = useState("");
    const [filterProductId, setFilterProductId] = useState("");

    const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

    const [adjustOpen, setAdjustOpen] = useState(false);
    const [adjustForm, setAdjustForm] = useState({ appCode: "", delta: "" });

    const [bonusOpen, setBonusOpen] = useState(false);
    const [bonusBalance, setBonusBalance] = useState("");
    const [bonusSubmitting, setBonusSubmitting] = useState(false);

    const [grantOpen, setGrantOpen] = useState(false);
    const [grantAppCode, setGrantAppCode] = useState("");
    const [grantCredits, setGrantCredits] = useState("");
    const [grantProductId, setGrantProductId] = useState("");
    const [grantSubmitting, setGrantSubmitting] = useState(false);
    const [productRows, setProductRows] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);

    const [consumeOpen, setConsumeOpen] = useState(false);
    const [consumeForm, setConsumeForm] = useState({ appCode: "", creditsConsumed: "" });
    const [consuming, setConsuming] = useState(false);
    const [moApps, setMoApps] = useState([]);

    const [resetTarget, setResetTarget] = useState(null);
    const [resetting, setResetting] = useState(false);

    const subjectUserId = typeof cloudUserId === "string" ? cloudUserId.trim() : "";

    const walletCards = useMemo(() => {
        if (!moApps.length) {
            return walletSummary;
        }
        const byCode = new Map(
            walletSummary.filter((wallet) => wallet.appCode).map((wallet) => [wallet.appCode, wallet]),
        );
        return moApps.map((app) => {
            const existing = byCode.get(app.code);
            if (existing) {
                return existing;
            }
            return {
                id: `app-${app.code}`,
                appCode: app.code,
                appName: app.name,
                creditBalance: 0,
                bonus: false,
            };
        });
    }, [moApps, walletSummary]);

    const showSnack = useCallback((severity, message) => {
        setSnackbar({ open: true, severity, message });
    }, []);

    const bumpReload = useCallback(() => setReloadToken((t) => t + 1), []);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const apps = await CreditService.listAllApps();
                if (!cancelled) setMoApps(apps);
            } catch (e) {
                Logger.error("MobileCreditsAdmin list apps:", e);
                if (!cancelled) setMoApps([]);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [reloadToken]);

    useEffect(() => {
        setPageModel((prev) => ({ ...prev, page: 0 }));
    }, [tab, filterAppCode, filterProductId, subjectUserId]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setProductsLoading(true);
            try {
                const raw = await CreditService.listProducts();
                const { rows: catalog } = CreditService.parseProductCatalog(raw);
                if (!cancelled) setProductRows(catalog);
            } catch (e) {
                Logger.error("MobileCreditsAdmin list products:", e);
                if (!cancelled) setProductRows([]);
            } finally {
                if (!cancelled) setProductsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [reloadToken]);

    const grantProductsForApp = useMemo(() => {
        const appCode = grantAppCode.trim().toLowerCase();
        if (!appCode || productRows.length === 0) return productRows;
        const matched = productRows.filter(
            (row) =>
                String(row.group || "").toLowerCase() === appCode ||
                String(row.group || "").toLowerCase().includes(appCode) ||
                String(row.productId || "").toLowerCase().includes(appCode.replace(/_/g, ".")),
        );
        return matched.length > 0 ? matched : productRows;
    }, [grantAppCode, productRows]);

    useEffect(() => {
        if (!subjectUserId) {
            setWalletSummary([]);
            setAdRewardByApp({});
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const result = await CreditService.listWallets(
                    { page: 0, pageSize: 100 },
                    [{ field: "updatedAt", sort: "desc" }],
                    { userId: subjectUserId, appCode: "" },
                );
                if (cancelled) return;
                setWalletSummary(result?.content ?? []);
                try {
                    const adResult = await CreditService.listAdRewards(
                        { page: 0, pageSize: 100 },
                        [{ field: "createdAt", sort: "desc" }],
                        { userId: subjectUserId, appCode: "" },
                    );
                    if (cancelled) return;
                    const byApp = {};
                    for (const row of adResult?.content ?? []) {
                        const code = row?.appCode || "jigsaw_muse";
                        if (!byApp[code]) byApp[code] = { count: 0, credits: 0 };
                        byApp[code].count += 1;
                        byApp[code].credits += Number(row.creditsAwarded) || 0;
                    }
                    setAdRewardByApp(byApp);
                } catch (adErr) {
                    Logger.warn("MobileCreditsAdmin ad rewards summary:", adErr);
                    if (!cancelled) setAdRewardByApp({});
                }
            } catch (e) {
                Logger.error("MobileCreditsAdmin wallet summary:", e);
                if (!cancelled) {
                    setWalletSummary([]);
                    setAdRewardByApp({});
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [subjectUserId, reloadToken]);

    useEffect(() => {
        if (!subjectUserId) {
            setRows([]);
            setRowCount(0);
            return;
        }

        let cancelled = false;

        async function load() {
            setLoading(true);
            try {
                const appCode = filterAppCode.trim();
                let result;
                if (tab === TAB_WALLETS) {
                    result = await CreditService.listWallets(pageModel, sort, {
                        userId: subjectUserId,
                        appCode,
                    });
                } else if (tab === TAB_IAP) {
                    result = await CreditService.listIap(pageModel, sort, {
                        userId: subjectUserId,
                        appCode,
                        productId: filterProductId.trim(),
                    });
                } else if (tab === TAB_AD) {
                    result = await CreditService.listAdRewards(pageModel, sort, {
                        userId: subjectUserId,
                        appCode,
                    });
                } else {
                    result = await CreditService.listUsage(pageModel, sort, {
                        userId: subjectUserId,
                        appCode,
                    });
                }
                if (cancelled) return;
                setRows(
                    (result?.content || []).map((row) => ({
                        ...row,
                        id: row.id,
                    })),
                );
                setRowCount(CreditService.totalElements(result));
            } catch (e) {
                Logger.error("MobileCreditsAdmin list:", e);
                if (!cancelled) {
                    setRows([]);
                    setRowCount(0);
                    showSnack("error", "Failed to load mobile credit data");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [tab, pageModel, sort, filterAppCode, filterProductId, subjectUserId, reloadToken, showSnack]);

    const handleTabChange = (_e, next) => {
        setTab(next);
        if (next === TAB_WALLETS) setSort([{ field: "updatedAt", sort: "desc" }]);
        else if (next === TAB_IAP) setSort([{ field: "purchaseDate", sort: "desc" }]);
        else if (next === TAB_AD) setSort([{ field: "createdAt", sort: "desc" }]);
        else setSort([{ field: "usedAt", sort: "desc" }]);
    };

    const openAdjust = (appCode = "") => {
        setAdjustForm({ appCode: appCode || "", delta: "" });
        setAdjustOpen(true);
    };

    const openBonusGrant = () => {
        const appCode = filterAppCode.trim();
        if (!appCode) return;
        const current = resolveWalletBalance(appCode, walletSummary, rows);
        setBonusBalance(String(current));
        setBonusOpen(true);
    };

    const openGrantCredit = () => {
        const appCode = filterAppCode.trim();
        setGrantAppCode(appCode);
        setGrantCredits("");
        setGrantProductId("");
        setGrantOpen(true);
    };

    const selectGrantProduct = (product) => {
        if (!product) return;
        setGrantProductId(product.productId || "");
        const credits = Number(product.creditsAwarded);
        setGrantCredits(Number.isFinite(credits) && credits > 0 ? String(credits) : "");
    };

    const handleGrantCredit = async () => {
        const appCode = grantAppCode.trim();
        const credits = Number(grantCredits);
        if (!subjectUserId || !appCode || !Number.isFinite(credits) || credits <= 0) {
            showSnack("warning", "App code and a positive credit amount are required");
            return;
        }

        setGrantSubmitting(true);
        try {
            const result = await CreditService.adjustWallet({
                userId: subjectUserId,
                appCode,
                delta: credits,
                bonus: false,
            });
            const err = apiFailureMessage(result);
            if (err) {
                showSnack("error", err);
                return;
            }
            const productNote = grantProductId ? ` (${grantProductId})` : "";
            showSnack(
                "success",
                `Granted +${formatCredits(credits)} credit${credits === 1 ? "" : "s"} to ${appCode}${productNote}`,
            );
            setGrantOpen(false);
            setGrantCredits("");
            setGrantProductId("");
            bumpReload();
        } catch (e) {
            Logger.error("MobileCreditsAdmin grant credit:", e);
            showSnack("error", e?.message || "Failed to grant credits");
        } finally {
            setGrantSubmitting(false);
        }
    };

    const handleGrantBonusBalance = async () => {
        const appCode = filterAppCode.trim();
        const targetBalance = Number(bonusBalance);
        if (!subjectUserId || !appCode || !Number.isFinite(targetBalance) || targetBalance < 0) {
            showSnack("warning", "App code and a non-negative balance are required");
            return;
        }

        const currentBalance = resolveWalletBalance(appCode, walletSummary, rows);
        const delta = targetBalance - currentBalance;
        if (delta === 0) {
            showSnack("info", "Balance is already at that amount");
            return;
        }

        setBonusSubmitting(true);
        try {
            const result = await CreditService.adjustWallet({
                userId: subjectUserId,
                appCode,
                delta,
                bonus: true,
            });
            const err = apiFailureMessage(result);
            if (err) {
                showSnack("error", err);
                return;
            }
            showSnack(
                "success",
                `Bonus balance set to ${formatCredits(targetBalance)} (${delta > 0 ? "+" : ""}${delta})`,
            );
            setBonusOpen(false);
            setBonusBalance("");
            bumpReload();
        } catch (e) {
            Logger.error("MobileCreditsAdmin grant bonus balance:", e);
            showSnack("error", "Failed to grant bonus balance");
        } finally {
            setBonusSubmitting(false);
        }
    };

    const openConsume = (appCode = "") => {
        setConsumeForm({ appCode: appCode || "", creditsConsumed: "" });
        setConsumeOpen(true);
    };

    const openReset = (wallet) => {
        if (!wallet?.appCode) return;
        setResetTarget({
            appCode: wallet.appCode,
            appName: wallet.appName || wallet.appCode,
        });
    };

    const handleResetApp = async () => {
        if (!resetTarget?.appCode || !subjectUserId) return;
        setResetting(true);
        try {
            const result = await CreditService.resetUserAppWallet({
                userId: subjectUserId,
                appCode: resetTarget.appCode,
            });
            const err = apiFailureMessage(result);
            if (err) {
                showSnack("error", err);
                return;
            }
            const parts = [];
            if (result?.walletRemoved) parts.push("wallet cleared");
            if (result?.iapTransactionsRemoved > 0) {
                parts.push(`${result.iapTransactionsRemoved} IAP row(s) removed`);
            }
            if (result?.usageLogsRemoved > 0) {
                parts.push(`${result.usageLogsRemoved} usage row(s) removed`);
            }
            showSnack(
                "success",
                parts.length
                    ? `Reset ${resetTarget.appName}: ${parts.join("; ")}.`
                    : `Reset ${resetTarget.appName}: nothing to clear.`,
            );
            setResetTarget(null);
            bumpReload();
        } catch (e) {
            Logger.error("MobileCreditsAdmin reset app:", e);
            showSnack("error", e?.message || "Failed to reset app");
        } finally {
            setResetting(false);
        }
    };

    const handleAdjustWallet = async () => {
        const appCode = adjustForm.appCode.trim();
        const delta = Number(adjustForm.delta);
        if (!subjectUserId || !appCode || !Number.isFinite(delta) || delta === 0) {
            showSnack("warning", "App code and non-zero delta are required");
            return;
        }
        try {
            const result = await CreditService.adjustWallet({ userId: subjectUserId, appCode, delta });
            const err = apiFailureMessage(result);
            if (err) {
                showSnack("error", err);
                return;
            }
            showSnack("success", `Balance updated (${delta > 0 ? "+" : ""}${delta})`);
            setAdjustOpen(false);
            setAdjustForm({ appCode: "", delta: "" });
            bumpReload();
        } catch (e) {
            Logger.error("MobileCreditsAdmin adjust wallet:", e);
            showSnack("error", "Failed to adjust wallet");
        }
    };

    const handleConsumeCredits = async () => {
        const appCode = consumeForm.appCode.trim();
        const creditsConsumed = Number(consumeForm.creditsConsumed);
        if (!subjectUserId || !appCode || !Number.isFinite(creditsConsumed) || creditsConsumed <= 0) {
            showSnack("warning", "App code and a positive credit amount are required");
            return;
        }
        setConsuming(true);
        try {
            const result = await CreditService.consumeCredits({
                userId: subjectUserId,
                appCode,
                creditsConsumed,
            });
            const err = apiFailureMessage(result);
            if (err) {
                showSnack("error", err);
                return;
            }
            const remaining =
                result?.creditBalance != null ? ` New balance: ${formatCredits(result.creditBalance)}` : "";
            showSnack("success", `Consumed ${creditsConsumed} credit${creditsConsumed === 1 ? "" : "s"}.${remaining}`);
            setConsumeOpen(false);
            setConsumeForm({ appCode: "", creditsConsumed: "" });
            bumpReload();
        } catch (e) {
            Logger.error("MobileCreditsAdmin consume credits:", e);
            showSnack("error", e?.message || "Failed to consume credits");
        } finally {
            setConsuming(false);
        }
    };

    const walletColumns = useMemo(
        () => [
            { field: "appCode", headerName: "App code", width: 200 },
            { field: "appName", headerName: "App name", flex: 1, minWidth: 160 },
            { field: "creditBalance", headerName: "Balance", width: 100, type: "number" },
            {
                field: "bonus",
                headerName: "Bonus",
                width: 88,
                valueFormatter: (value) => (value ? "Yes" : "No"),
            },
            {
                field: "updatedAt",
                headerName: "Updated",
                width: 130,
                valueFormatter: (value) => formatEpochMs(value),
            },
            {
                field: "actions",
                headerName: "Actions",
                width: 96,
                sortable: false,
                filterable: false,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            width: "100%",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Tooltip title="Adjust balance">
                            <IconButton
                                size="small"
                                aria-label="Adjust balance"
                                onClick={() => openAdjust(params.row.appCode || "")}
                            >
                                <TuneIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Force consume (testing purpose only)">
                            <IconButton
                                size="small"
                                aria-label="Force consume"
                                color="warning"
                                onClick={() => openConsume(params.row.appCode || "")}
                            >
                                <RemoveCircleOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ),
            },
        ],
        [],
    );

    const iapColumns = useMemo(
        () => [
            { field: "transactionId", headerName: "Transaction ID", flex: 1, minWidth: 200 },
            { field: "appCode", headerName: "App", width: 160 },
            { field: "productId", headerName: "Product", width: 200,
                valueGetter: (_value, row) =>
                    creditProductDisplayLabel({ productId: row?.productId }) === "Product"
                        ? row?.productId
                        : `${creditProductDisplayLabel({ productId: row?.productId })} (${row?.productId})`,
            },
            { field: "creditsAwarded", headerName: "Credits", width: 90, type: "number" },
            {
                field: "purchaseDate",
                headerName: "Purchased",
                width: 130,
                valueFormatter: (value) => formatEpochMs(value),
            },
        ],
        [],
    );

    const adRewardColumns = useMemo(
        () => [
            { field: "eventId", headerName: "Event ID", flex: 1, minWidth: 200 },
            { field: "appCode", headerName: "App", width: 160 },
            { field: "creditsAwarded", headerName: "Credits", width: 90, type: "number" },
            { field: "deviceId", headerName: "Device / install", flex: 1, minWidth: 180 },
            {
                field: "createdAt",
                headerName: "Watched",
                width: 130,
                valueFormatter: (value) => formatEpochMs(value),
            },
        ],
        [],
    );

    const usageColumns = useMemo(
        () => [
            { field: "appCode", headerName: "App", width: 160 },
            {
                field: "puzzleTitle",
                headerName: "Puzzle title",
                flex: 1,
                minWidth: 180,
                valueGetter: (_value, row) => usageLogPuzzleTitle(row),
            },
            {
                field: "sizeDifficulty",
                headerName: "Size / difficulty",
                width: 150,
                valueGetter: (_value, row) => formatUsageLogSizeDifficulty(row),
            },
            { field: "featureUsed", headerName: "Feature", width: 160 },
            { field: "creditsConsumed", headerName: "Credits", width: 90, type: "number" },
            {
                field: "usedAt",
                headerName: "Used at",
                width: 130,
                valueFormatter: (value) => formatEpochMs(value),
            },
        ],
        [],
    );

    if (!subjectUserId) {
        return null;
    }

    return (
        <Card sx={{ mt: 3 }}>
            <CardContent>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                        mb: 1,
                    }}
                >
                    <Typography variant="h5" component="h2" sx={{ m: 0 }}>
                        Mobile Credit
                    </Typography>
                    <Link
                        component={RouterLink}
                        to="/admin/account/credit"
                        variant="body2"
                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                    >
                        All mobile credit
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </Link>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Native app wallets, IAP purchases, Watch-ad rewards, and credit usage for this user (
                    <code>/v1/credit</code>). App codes ending in <code>_hints</code> (e.g.{" "}
                    <code>sliding_puzzle_quest_hints</code>, <code>jigsaw_muse_hints</code>,{" "}
                    <code>web_app_hints</code>) are ghost-guide / placement-guide hint credits —
                    separate from play-unlock credits on the main app wallet. Jigsaw Muse Watch-ad
                    grants (+2) are listed under <strong>Watch ad</strong> and also as IAP product{" "}
                    <code>admob_rewarded</code>.
                </Typography>

                {walletCards.length > 0 ? (
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                        {walletCards.map((wallet) => (
                            <Grid key={wallet.id ?? `${wallet.appCode}-${wallet.userId}`} size={{ xs: 12, sm: 6, md: 4 }}>
                                <Box
                                    sx={{
                                        position: "relative",
                                        p: 1.5,
                                        pr: 5,
                                        borderRadius: 1,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        bgcolor: "action.hover",
                                    }}
                                >
                                    {wallet.appCode ? (
                                        <Tooltip title="Reset app (testing)">
                                            <IconButton
                                                size="small"
                                                aria-label={`Reset ${wallet.appName || wallet.appCode}`}
                                                onClick={() => openReset(wallet)}
                                                sx={{
                                                    position: "absolute",
                                                    top: 4,
                                                    right: 4,
                                                    color: "text.secondary",
                                                }}
                                            >
                                                <RestartAltIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : null}
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {wallet.appName || wallet.appCode || "App"}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                                        <Typography variant="h6" component="p" sx={{ fontWeight: 700, m: 0 }}>
                                            {formatCredits(wallet.creditBalance)}
                                        </Typography>
                                        {wallet.appCode ? (
                                            <Chip label={wallet.appCode} size="small" variant="outlined" />
                                        ) : null}
                                        {wallet.bonus ? (
                                            <Chip label="Bonus" size="small" color="secondary" />
                                        ) : null}
                                        {adRewardByApp[wallet.appCode]?.count > 0 ? (
                                            <Chip
                                                label={`Watch ad +${formatCredits(adRewardByApp[wallet.appCode].credits)}`}
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                            />
                                        ) : null}
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Loading mobile apps…
                    </Typography>
                )}

                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 2 }}
                >
                    <Tab label="Wallets" />
                    <Tab label="IAP transactions" />
                    <Tab label="Watch ad" />
                    <Tab label="Usage log" />
                </Tabs>

                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                    }}
                >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                        <AppCodeSelect
                            apps={moApps}
                            value={filterAppCode}
                            onChange={setFilterAppCode}
                            label="App code"
                            allowAll
                            fullWidth={false}
                            size="small"
                            sx={{ minWidth: 280 }}
                        />
                        <Tooltip title="Grant credits from a product pack (or custom amount)">
                            <span>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<CardGiftcardOutlinedIcon />}
                                    onClick={openGrantCredit}
                                >
                                    Grant Credit
                                </Button>
                            </span>
                        </Tooltip>
                        {tab === TAB_WALLETS ? (
                            <Tooltip title="Set balance and mark wallet as bonus (testing)">
                                <span>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<CardGiftcardOutlinedIcon />}
                                        disabled={!filterAppCode.trim()}
                                        onClick={openBonusGrant}
                                    >
                                        Grant bonus
                                    </Button>
                                </span>
                            </Tooltip>
                        ) : null}
                    </Box>
                    {tab === TAB_IAP ? (
                        <TextField
                            size="small"
                            label="Product ID"
                            value={filterProductId}
                            onChange={(e) => setFilterProductId(e.target.value)}
                            sx={{ minWidth: 240 }}
                        />
                    ) : null}
                </Box>

                <DataGrid
                    rows={rows}
                    columns={
                        tab === TAB_WALLETS
                            ? walletColumns
                            : tab === TAB_IAP
                              ? iapColumns
                              : tab === TAB_AD
                                ? adRewardColumns
                                : usageColumns
                    }
                    sx={{
                        "& .MuiDataGrid-cell[data-field='actions']": {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        },
                    }}
                    rowCount={rowCount}
                    paginationModel={pageModel}
                    onPaginationModelChange={setPageModel}
                    pageSizeOptions={[25, 50, 100]}
                    paginationMode="server"
                    sortingMode="server"
                    sortModel={sort}
                    onSortModelChange={setSort}
                    loading={loading}
                    disableRowSelectionOnClick
                    autoHeight
                    hideFooterSelectedRowCount
                />
            </CardContent>

            <Dialog
                open={grantOpen}
                onClose={() => {
                    if (!grantSubmitting) setGrantOpen(false);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Grant Credit</DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        pt: 3,
                        overflow: "visible",
                    }}
                >
                    <Alert severity="info">
                        Add credits to this user&apos;s wallet. Pick a product pack below to use its
                        credit amount, or enter a custom amount.
                    </Alert>
                    <TextField label="User ID" value={subjectUserId} fullWidth disabled />
                    <AppCodeSelect
                        apps={moApps}
                        value={grantAppCode}
                        onChange={(appCode) => {
                            setGrantAppCode(appCode);
                            setGrantProductId("");
                        }}
                        label="App code"
                        autoFocus
                    />
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                            Available products
                        </Typography>
                        {productsLoading ? (
                            <Typography variant="body2" color="text.secondary">
                                Loading product catalog…
                            </Typography>
                        ) : grantProductsForApp.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No products found in the catalog.
                            </Typography>
                        ) : (
                            <Stack spacing={1}>
                                {grantProductsForApp.map((product) => {
                                    const selected = grantProductId === product.productId;
                                    const credits = Number(product.creditsAwarded) || 0;
                                    return (
                                        <Button
                                            key={product.id || product.productId}
                                            variant={selected ? "contained" : "outlined"}
                                            color={selected ? "primary" : "inherit"}
                                            onClick={() => selectGrantProduct(product)}
                                            sx={{
                                                justifyContent: "space-between",
                                                textTransform: "none",
                                                px: 1.5,
                                                py: 1.25,
                                            }}
                                        >
                                            <Box sx={{ textAlign: "left", minWidth: 0 }}>
                                                <Typography
                                                    component="span"
                                                    sx={{ fontWeight: 700, display: "block" }}
                                                >
                                                    {product.label || product.productId}
                                                </Typography>
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                    color={selected ? "inherit" : "text.secondary"}
                                                    sx={{
                                                        display: "block",
                                                        opacity: selected ? 0.9 : 1,
                                                    }}
                                                >
                                                    {product.productId}
                                                    {product.group ? ` · ${product.group}` : ""}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                size="small"
                                                label={`+${formatCredits(credits)}`}
                                                color={selected ? "default" : "success"}
                                                sx={{
                                                    ml: 1,
                                                    fontWeight: 700,
                                                    ...(selected
                                                        ? { bgcolor: "rgba(255,255,255,0.25)" }
                                                        : null),
                                                }}
                                            />
                                        </Button>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>
                    <TextField
                        label="Credits to grant"
                        type="number"
                        value={grantCredits}
                        onChange={(e) => {
                            setGrantCredits(e.target.value);
                            setGrantProductId("");
                        }}
                        helperText={
                            grantProductId
                                ? `From product ${grantProductId}`
                                : "Select a product above, or type a custom amount"
                        }
                        fullWidth
                        slotProps={{ htmlInput: { min: 1 } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGrantOpen(false)} disabled={grantSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleGrantCredit}
                        disabled={grantSubmitting || !grantAppCode.trim()}
                    >
                        {grantSubmitting ? "Granting…" : "Grant credits"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={bonusOpen}
                onClose={() => {
                    if (!bonusSubmitting) setBonusOpen(false);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Grant bonus balance</DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        pt: 3,
                        overflow: "visible",
                    }}
                >
                    <Alert severity="warning">Testing purpose only.</Alert>
                    <Alert severity="info">
                        Sets the wallet balance for the selected app and marks the{" "}
                        <code>mo_user_credits.bonus</code> row as <strong>true</strong>.
                    </Alert>
                    <TextField label="User ID" value={subjectUserId} fullWidth disabled />
                    <TextField label="App code" value={filterAppCode.trim()} fullWidth disabled />
                    <TextField
                        label="Balance"
                        type="number"
                        value={bonusBalance}
                        onChange={(e) => setBonusBalance(e.target.value)}
                        helperText={`Current balance: ${formatCredits(
                            resolveWalletBalance(filterAppCode.trim(), walletSummary, rows),
                        )}`}
                        fullWidth
                        autoFocus
                        slotProps={{ htmlInput: { min: 0 } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBonusOpen(false)} disabled={bonusSubmitting}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleGrantBonusBalance} disabled={bonusSubmitting}>
                        {bonusSubmitting ? "Applying…" : "Apply bonus balance"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={adjustOpen} onClose={() => setAdjustOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Adjust wallet balance</DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        pt: 3,
                        overflow: "visible",
                    }}
                >
                    <Alert severity="info">
                        Admin correction only: add or remove credits with a signed delta. Use for grants,
                        refunds, or fixing mistakes. Does not write to the Usage log.
                    </Alert>
                    <TextField label="User ID" value={subjectUserId} fullWidth disabled />
                    <AppCodeSelect
                        apps={moApps}
                        value={adjustForm.appCode}
                        onChange={(appCode) => setAdjustForm((f) => ({ ...f, appCode }))}
                        label="App"
                        autoFocus
                    />
                    <TextField
                        label="Delta"
                        type="number"
                        value={adjustForm.delta}
                        onChange={(e) => setAdjustForm((f) => ({ ...f, delta: e.target.value }))}
                        helperText="Positive to grant credits; negative to deduct (balance cannot go below 0). e.g. +3 or -2"
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAdjustOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAdjustWallet}>
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={consumeOpen}
                onClose={() => {
                    if (!consuming) setConsumeOpen(false);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Force consume credits</DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        pt: 3,
                        overflow: "visible",
                    }}
                >
                    <Alert severity="warning">Testing purpose only.</Alert>
                    <Alert severity="info">
                        Simulates in-app credit spend: deducts only and fails if the balance is too low.
                        Creates a Usage log row (<code>admin_force_consume</code>). Use to test consumption
                        or record a real spend—not for grants or balance corrections (use Adjust balance
                        instead).
                    </Alert>
                    <TextField label="User ID" value={subjectUserId} fullWidth disabled />
                    <AppCodeSelect
                        apps={moApps}
                        value={consumeForm.appCode}
                        onChange={(appCode) => setConsumeForm((f) => ({ ...f, appCode }))}
                        label="App"
                        autoFocus
                    />
                    <TextField
                        label="Credits"
                        type="number"
                        value={consumeForm.creditsConsumed}
                        onChange={(e) => setConsumeForm((f) => ({ ...f, creditsConsumed: e.target.value }))}
                        helperText="Positive amount only. Fails if the wallet balance is insufficient."
                        fullWidth
                        slotProps={{ htmlInput: { min: 1 } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConsumeOpen(false)} disabled={consuming}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="warning" onClick={handleConsumeCredits} disabled={consuming}>
                        {consuming ? "Consuming…" : "Consume"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(resetTarget)}
                onClose={() => {
                    if (!resetting) setResetTarget(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reset mobile app</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Warning: this will reset{" "}
                        <strong>{resetTarget?.appName || resetTarget?.appCode || "this app"}</strong> for
                        this user. Credits will be removed and purchase history will be deleted. This is
                        for testing purposes only.
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                        App code: <code>{resetTarget?.appCode}</code>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        IAP transactions and usage logs for this app will also be removed. This cannot be
                        undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetTarget(null)} disabled={resetting}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="error" onClick={handleResetApp} disabled={resetting}>
                        {resetting ? "Resetting…" : "Reset app"}
                    </Button>
                </DialogActions>
            </Dialog>

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Card>
    );
}
